import React, { useState } from "react";
import {
  Button,
  Col,
  Form,
  Row,
  Spinner,
  Table,
  Container,
} from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import { useLeagueMembership } from "../context/LeagueMembershipContext";
import ModalDialog from "../Common/ModalDialog";
import { useRequest } from "../hooks/useRequest";
import { Link } from "react-router-dom";
import { getSeasonsUrl, getSeasonUrl } from "../config/api";
import { useLeague } from "../hooks/useLeague";
import { apiPost, apiPut, apiDelete } from "../utils/apiClient";
import { PaginatedResponse } from "../types/api";

function TableLoadingView() {
  return (
    <Container>
      <Row className="justify-content-md-center">
        <Col md="auto">
          <Spinner animation="grow" /> Loading...
        </Col>
      </Row>
    </Container>
  );
}

function AdminSeasonsPage() {
  const auth = useAuth();
  const { currentLeague, setSelectedSeasonId } = useLeague();

  function SeasonsList({
    // refreshListToken,
    onCreateClick,
    onEditClick,
    onDeleteClick,
  }: {
    // refreshListToken: string;
    onCreateClick: () => void;
    onEditClick: (season: any) => void;
    onDeleteClick: (season: any) => void;
  }) {
    // const [seasons, setSeasons] = useState<any>([]);
    const { canManageLeague } = useLeagueMembership();
    const { data: response, error } = useRequest<PaginatedResponse<any>>(
      currentLeague ? getSeasonsUrl(currentLeague.id) : undefined,
      auth.token,
    );

    return (
      <div className="admin-content">
        <Container>
          <Row>
            <Col md={8}>
              {canManageLeague && (
                <Row className="mb-2">
                  <Col className="d-flex justify-content-end">
                    <Button onClick={() => onCreateClick()} variant="primary">
                      New
                    </Button>
                  </Col>
                </Row>
              )}
              <Row>
                <Col>
                  {error && <p>There is an error.</p>}
                  {!response && <TableLoadingView />}
                  {response &&
                    response.data.length === 0 &&
                    (canManageLeague ? (
                      <p>Click `New` to create your first season.</p>
                    ) : (
                      <p>This league has no seasons yet.</p>
                    ))}
                  {response && response.data.length > 0 && (
                    <Table striped bordered hover className="crud">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {response.data.map((season: any, index: number) => {
                          return (
                            <tr key={index}>
                              <td>{season.name}</td>
                              <td align="right">
                                <Link
                                  to={`${season.id}/overview`}
                                  onClick={() => setSelectedSeasonId(season.id)}
                                >
                                  <Button variant="secondary">
                                    {canManageLeague ? "Manage" : "View"}
                                  </Button>
                                </Link>
                                {canManageLeague && (
                                  <>
                                    &nbsp;
                                    <Button onClick={() => onEditClick(season)}>
                                      Edit Name
                                    </Button>
                                    &nbsp;
                                    <Button
                                      variant="danger"
                                      onClick={() => onDeleteClick(season)}
                                    >
                                      Delete
                                    </Button>
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  interface SeasonForm extends HTMLFormElement {
    nameField: HTMLInputElement;
  }

  function SeasonsFormView({
    season,
    onCancelClick,
  }: {
    season?: any;
    onCancelClick: () => void;
  }) {
    const [errorMessage, setErrorMessage] = useState("");
    const handleSubmit: React.FormEventHandler<SeasonForm> = (event) => {
      event.preventDefault();
      const name = event.currentTarget.nameField.value;
      if (!name || name.length === 0) {
        setErrorMessage("Name is required");
        return;
      }

      if (!currentLeague) {
        setErrorMessage("No league selected");
        return;
      }

      const isEdit = season?.id || false;

      const url = isEdit
        ? getSeasonUrl(season.id)
        : getSeasonsUrl(currentLeague.id);

      const apiFn = isEdit ? apiPut : apiPost;

      apiFn(
        url,
        { season: { name } },
        { token: auth.token, leagueAbbr: currentLeague.abbr },
      )
        .then((result) => {
          onCancelClick();
        })
        .catch((error) => {
          console.error(error);
          setErrorMessage(error.message || error);
        });
    };
    return (
      <Container>
        <Row>
          <Col md={8}>
            <h1>Season Form</h1>

            {errorMessage && (
              <div className="alert alert-warning" role="alert">
                {errorMessage}
              </div>
            )}
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3" controlId="nameField">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Name"
                  defaultValue={season?.name}
                />
                <Form.Text className="text-muted">i.e. 2021-2022</Form.Text>
              </Form.Group>
              <Button onClick={() => onCancelClick()} variant="secondary">
                Cancel
              </Button>{" "}
              <Button variant="primary" type="submit">
                Submit
              </Button>
            </Form>
          </Col>
        </Row>
      </Container>
    );
  }

  function showForm(season?: any) {
    setContent(
      <SeasonsFormView
        season={season}
        onCancelClick={showList}
      ></SeasonsFormView>,
    );
  }
  function showList() {
    setContent(
      <SeasonsList
        onCreateClick={showForm}
        onEditClick={showForm}
        onDeleteClick={confirmDelete}
      ></SeasonsList>,
    );
  }

  function confirmDelete(season: any) {
    setContent(
      <div
        className="modal show"
        style={{ display: "block", position: "initial" }}
      >
        <ModalDialog
          titleText="Delete Season Confirmation"
          bodyText={`Are you sure you want to delete this season? (${season.name})`}
          onCancelClick={() => {
            console.debug("Delete canceled");
            showList();
          }}
          onSaveClick={() => {
            console.debug("Delete the Season", season);

            apiDelete(getSeasonUrl(season.id), {
              token: auth.token,
              ...(currentLeague?.abbr
                ? { leagueAbbr: currentLeague.abbr }
                : {}),
            })
              .then(() => {
                showList();
              })
              .catch((error) => {
                console.error(error);
              });
          }}
        ></ModalDialog>
      </div>,
    );
  }
  const [content, setContent] = useState(
    <SeasonsList
      onCreateClick={showForm}
      onEditClick={showForm}
      onDeleteClick={confirmDelete}
    ></SeasonsList>,
  );

  return <>{content}</>;
}

export default AdminSeasonsPage;
